import React from 'react';
import { css } from '@emotion/core';

import { Icon, breakpoints, colors, durations } from '../../../design-system';

type Props = {
  pages: number[];
  goToPage: (page: number) => void;
  currentPage: number;
  totalPages: number;
};

export const ReviewsNavigation = ({ pages, goToPage, currentPage, totalPages }: Props) => (
  <ul
    role="list"
    aria-label="Pagination"
    css={css`
      display: flex;
      gap: 4px;
      padding-inline-start: 0;
      height: 44px;
      li {
        transition: background-color ${durations.FOCUS_DELAY}ms ease-in-out;

        @media (min-width: ${breakpoints.S}px) {
          border-radius: 4px;
          padding: 0 4px;
        }
      }
    `}
  >
    <li
      css={css`
        opacity: ${currentPage === 1 ? 0.5 : 1};
        display: flex;
        align-items: center;
        @media (hover: hover) {
          &:hover {
            background-color: ${currentPage === 1 ? 'initial' : colors.BACKGROUND};
          }
        }
      `}
    >
      <button
        onClick={() => currentPage > 1 && goToPage(currentPage - 1)}
        type="button"
        aria-label="bouton pour naviguer sur la page précédente"
        data-testid="Button to navigate to the previous page"
        disabled={currentPage === 1}
        css={css`
          background: none;
          padding: 0;
          border: none;
          color: ${colors.BLACK};
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 44px;
          gap: 4px;
          cursor: ${currentPage === 1 ? 'initial' : 'pointer'};
        `}
      >
        <Icon name="chevronLeft" size={14} />
        <span
          css={css`
            color: ${colors.BLACK};
            font-size: 1.4rem;
          `}
        >
          Prec
        </span>
      </button>
    </li>
    {pages.map((page, index) => (
      <li
        key={index}
        css={css`
          display: flex;
          @media (hover: hover) {
            &:hover {
              background-color: ${currentPage === page ? 'initial' : colors.BACKGROUND};
            }
          }
        `}
      >
        {page - pages[index - 1] > 1 && (
          <span
            css={css`
              user-select: none;
              font-size: 1.4rem;
              color: ${colors.BLACK};
              align-self: center;
              @media (hover: hover) {
                &:hover {
                  cursor: 'pointer';
                }
              }
            `}
          >
            ..
          </span>
        )}
        <button
          css={css`
            background: none;
            padding: 0 2px;
            border: none;
            color: ${colors.BLACK};
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 44px;
            font-size: 1.4rem;
            opacity: ${currentPage === page ? 1 : 0.5};
            cursor: ${currentPage === page ? 'initial' : 'pointer'};
          `}
          disabled={currentPage === page}
          aria-label={`bouton pour naviguer sur la page ${page}`}
          data-testid={`Button to navigate to page ${page}`}
          onClick={() => goToPage(page)}
        >
          {page}
        </button>
      </li>
    ))}
    <li
      css={css`
        opacity: ${currentPage === totalPages ? 0.5 : 1};
        display: flex;
        align-items: center;
        @media (hover: hover) {
          &:hover {
            background-color: ${currentPage === totalPages ? 'initial' : colors.BACKGROUND};
          }
        }
      `}
    >
      <button
        onClick={() => currentPage < totalPages && goToPage(currentPage + 1)}
        type="button"
        aria-label="bouton pour naviguer sur la page suivante"
        data-testid="Button to navigate to the next page"
        disabled={currentPage === totalPages}
        css={css`
          background: none;
          padding: 0;
          border: none;
          color: ${colors.BLACK};
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 44px;
          gap: 4px;
          cursor: ${currentPage === totalPages ? 'initial' : 'pointer'};
        `}
      >
        <span
          css={css`
            color: ${colors.BLACK};
            font-size: 1.4rem;
          `}
        >
          Suiv
        </span>
        <Icon name="chevronRight" size={14} />
      </button>
    </li>
  </ul>
);
